import Modal from "@mui/material/Modal";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { closeDialog } from "../../../store/slices/dialog/dialogSlice";
import { EDIT_DAYOFF } from "../../../store/slices/utils/dialogEnum";
import {
  useFetchUserDayoffsQuery,
  useUserRightBulkUpdateMutation,
} from "../../../store";
import Loading from "../../../components/shared/Loading";
import { useFormik } from "formik";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { REPORT, UNPAID } from "../../../dayofftypes";
import { openErrorToast, openSuccessToast } from "../../../store/slices/toast/toastSlice";
import { editValidationSchema } from "../validations/editDayoffValidations";

const EditDayoffs = () => {
  const dispatch = useDispatch();
  const { editDayoffDialogOpen } = useSelector((state) => state.dialogSlice);
  const { selectedUserId } = useSelector((state) => state.userSlice);
  const { data, isLoading } = useFetchUserDayoffsQuery(selectedUserId);
  const [bulkUpdateDayoff, { isLoading: isUpdating }] =
    useUserRightBulkUpdateMutation();
  const handleClose = () => {
    dispatch(closeDialog(EDIT_DAYOFF));
  };

  const columns = [
    { label: "İzin Türü", minWidth: 170 },
    { label: "Toplam İzin Hakkı", minWidth: 120 },
    { label: "Kullanılan İzin", minWidth: 120 },
    { label: "Kalan İzin", minWidth: 120 },
  ];

  const formik = useFormik({
    initialValues: {
      forms:
        data?.data
          ?.filter(
            (dayoff) =>
              dayoff.dayoffTypeId !== REPORT && dayoff.dayoffTypeId !== UNPAID
          )
          .map((dayoff) => ({
            id: dayoff.id,
            name: dayoff.dayoffTypeName,
            totalRight: dayoff.dayoffRightCount,
            usedRight: dayoff.dayoffUsedCount,
          })) ?? [],
    },
    enableReinitialize: true,
    validationSchema: editValidationSchema,
    onSubmit: async (values) => {
      // Güncelleme işlemleri burada yapılacak
      bulkUpdateDayoff(values.forms).then((res) => {
        if (res) {
          if (!res.data.isSuccess) {
            dispatch(openErrorToast(res.data.message));
          } else {
            dispatch(openSuccessToast("İzinler Güncellendi"));
            handleClose();
          }
        }
      });
    },
  });

  // Kalan izin hesaplama
  const getUsableDayoff = (total, used) => {
    const kalan = Number(total) - Number(used);
    return kalan < 0 ? 0 : kalan;
  };

  if (isLoading) return <Loading isLoading={isLoading} />;
  return (
    <Modal
      open={editDayoffDialogOpen}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "#f8fafc",
          borderRadius: 4,
          boxShadow: 8,
          p: { xs: 2, sm: 4 },
          minWidth: { xs: 320, sm: 600 },
          maxWidth: "90vw",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "primary.main",
            mb: 2,
            textAlign: "center",
            letterSpacing: 1,
          }}
        >
          İzin Hakları Düzenle
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <Paper sx={{ width: "100%", overflow: "hidden", mb: 2 }}>
            <TableContainer sx={{ maxHeight: 440 }}>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    {columns.map((column, index) => (
                      <TableCell
                        key={index}
                        align="center"
                        style={{ minWidth: column.minWidth, fontWeight: 700 }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formik.values.forms.map((form, idx) => (
                    <TableRow key={form.id}>
                      <TableCell align="center">{form.name}</TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          name={`forms[${idx}].totalRight`}
                          value={form.totalRight}
                          onChange={formik.handleChange}
                          inputProps={{
                            min: 1,
                            style: { textAlign: "center" },
                          }}
                          size="small"
                          variant="outlined"
                          sx={{ width: 80 }}
                          error={
                            formik.errors.forms &&
                            Boolean(formik.errors.forms[idx]?.totalRight)
                          }
                          helperText={
                            formik.touched.forms &&
                            formik.touched.forms[idx]?.totalRight &&
                            formik.errors.forms &&
                            formik.errors.forms[idx]?.totalRight
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          name={`forms[${idx}].usedRight`}
                          value={form.usedRight}
                          onChange={formik.handleChange}
                          inputProps={{
                            min: 0,
                            max: form.totalRight,
                            style: { textAlign: "center" },
                          }}
                          size="small"
                          variant="outlined"
                          sx={{ width: 80 }}
                          error={
                            formik.errors.forms &&
                            Boolean(formik.errors.forms[idx]?.usedRight)
                          }
                          helperText={
                            formik.touched.forms &&
                            formik.touched.forms[idx]?.usedRight &&
                            formik.errors.forms &&
                            formik.errors.forms[idx]?.usedRight
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          value={getUsableDayoff(
                            form.totalRight,
                            form.usedRight
                          )}
                          size="small"
                          variant="outlined"
                          sx={{
                            width: 80,
                            "& .MuiInputBase-input.Mui-disabled": {
                              color: "success.main",
                              fontWeight: 700,
                            },
                          }}
                          disabled
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              loading={isUpdating}
              color="primary"
              sx={{
                fontWeight: 700,
                borderRadius: 2,
                px: 4,
                py: 1,
                boxShadow: 2,
              }}
            >
              Kaydet
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default EditDayoffs;
